/**
 * Snapshot HTML is sanitised on the SUNDAE side before it ever reaches this
 * app (plan §3b: zod-enforced allowlist, remote images stripped, `cid:` images
 * rewritten to `attachment:<id>` placeholders). So there is no DOMPurify pass
 * here — only the split into <style> tags and body markup that the iframe
 * srcdoc needs, lifted from the mail app's `lib/emailHtmlSanitizer`.
 */

export interface SanitizedEmail {
  styles: string;
  body: string;
}

/**
 * Pull <style> tags out of a full HTML document and return them separately
 * from the body markup, so the iframe can put styles in its <head>.
 */
export function extractStylesAndBody(html: string): SanitizedEmail {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const headStyleTags = Array.from(doc.head?.querySelectorAll('style') || []);
  const bodyStyleTags = Array.from(doc.body?.querySelectorAll('style') || []);
  const allStyleTags = [...headStyleTags, ...bodyStyleTags];

  const styles = allStyleTags
    .map((style) => {
      const css = style.textContent || style.innerHTML || '';
      return css.trim() ? `<style>${css}</style>` : '';
    })
    .filter(Boolean)
    .join('\n');

  bodyStyleTags.forEach((style) => style.remove());

  const body = doc.body?.innerHTML || html;

  return { styles, body };
}

/** Plain text bodies still need to render as HTML inside the iframe. */
export function textToHtml(text: string): SanitizedEmail {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return { styles: '', body: `<pre style="white-space:pre-wrap;font-family:inherit">${escaped}</pre>` };
}

/** Every `attachment:<id>` placeholder the sanitised HTML references. */
export function referencedAttachmentIds(html: string): string[] {
  const ids = new Set<string>();
  for (const match of html.matchAll(/attachment:([A-Za-z0-9_-]+)/g)) {
    ids.add(match[1]);
  }
  return [...ids];
}

/**
 * Swap the placeholders for URLs the iframe can actually load. The blob route
 * needs an Authorization header that an <img src> cannot carry, so the caller
 * fetches each attachment and passes object URLs in.
 */
export function resolveAttachmentPlaceholders(html: string, urls: Map<string, string>): string {
  return html.replace(/attachment:([A-Za-z0-9_-]+)/g, (whole, id: string) => urls.get(id) ?? whole);
}
